import { useState } from "react";
import {
  PRODUCT_ICON_MAP, PRODUCT_COLOR_MAP, PRODUCT_SHORT_LABELS,
  classifyTraveler, formatCurrency, calcNights, Icons,
} from "./bookingConstants";
import { useToast } from "../../context/ToastContext";
import s from "./StepSummary.module.css";

/* ═══════════════════════════════════════════════════════════════════════════
   HELPERS
   ═══════════════════════════════════════════════════════════════════════════ */

const fmtDate = (d) => {
  if (!d) return "—";
  const parts = d.split("-");
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return d;
};

const fmtDateFull = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "2-digit", year: "numeric" });
  } catch { return fmtDate(d); }
};

const getDayNum = (d) => {
  if (!d) return "—";
  const parts = d.split("-");
  return parts.length === 3 ? parts[2] : "—";
};

/* ─── Palettes ─────────────────────────────────────────────────────────── */

const STATUS_PALETTE = {
  Confirmed:        { color: "#059669", bg: "#ecfdf5", border: "#a7f3d0" },
  Pending:          { color: "#d97706", bg: "#fef3c7", border: "#fde68a" },
  Option:           { color: "#6366f1", bg: "#eef2ff", border: "#c7d2fe" },
  Cancelled:        { color: "#dc2626", bg: "#fef2f2", border: "#fecaca" },
  Ticketed:         { color: "#0891b2", bg: "#ecfeff", border: "#a5f3fc" },
  Unpaid:           { color: "#d97706", bg: "#fef3c7", border: "#fde68a" },
  "Partially Paid": { color: "#6366f1", bg: "#eef2ff", border: "#c7d2fe" },
  Paid:             { color: "#059669", bg: "#ecfdf5", border: "#a7f3d0" },
  Draft:            { color: "#6b7280", bg: "#f3f4f6", border: "#d1d5db" },
};

const PAX_PALETTE = {
  ADT: { color: "#3b5bdb", bg: "#eef3ff", label: "Adult", desc: "12+ years" },
  CHD: { color: "#d97706", bg: "#fffbeb", label: "Child", desc: "2–11 years" },
  INF: { color: "#059669", bg: "#ecfdf5", label: "Infant", desc: "Under 2" },
  "—": { color: "#868c98", bg: "#f0f1f4", label: "Unknown", desc: "No DOB" },
};

const PAX_ORDER = ["ADT", "CHD", "INF"];
const PAX_NOUN = { ADT: ["Adult", "Adults"], CHD: ["Child", "Children"], INF: ["Infant", "Infants"] };
const PAX_OV_DESC = { ADT: "12+ Years", CHD: "2 – 11 Years", INF: "0 – 23 Months" };

/* ═══════════════════════════════════════════════════════════════════════════
   SHARED SUB-COMPONENTS
   ═══════════════════════════════════════════════════════════════════════════ */

/* Badge — status pill with dot */
const Badge = ({ label, custom }) => {
  const c = custom || STATUS_PALETTE[label] || { color: "#525866", bg: "#f0f1f4", border: "#e2e4e9" };
  return (
    <span className={s.badge} style={{ color: c.color, background: c.bg, borderColor: c.border }}>
      <span className={s.badgeDot} style={{ background: c.color }} />
      {label}
    </span>
  );
};

/* Pill — small metadata tag */
const Pill = ({ children, color = "#525866", bg = "#f0f1f4" }) => (
  <span className={s.pill} style={{ color, background: bg }}>{children}</span>
);

/* ═══════════════════════════════════════════════════════════════════════════
   PRODUCT DETAIL RENDERERS
   ═══════════════════════════════════════════════════════════════════════════ */

/* ─── Flight Detail ───────────────────────────────────────────────────── */

const FLIGHT_AIRPORT_CITY = {
  BRU: "Brussels", AMS: "Amsterdam", LHR: "London", CDG: "Paris",
  FRA: "Frankfurt", AYT: "Antalya", DXB: "Dubai", IST: "Istanbul",
  BCN: "Barcelona", MAD: "Madrid", HRG: "Hurghada", TUN: "Tunis",
  LIN: "Milano", FCO: "Rome", MUC: "Munich", VIE: "Vienna",
  ZRH: "Zurich", ATH: "Athens", SSH: "Sharm El Sheikh",
};
const flightCityName = (code) => FLIGHT_AIRPORT_CITY[code] || code;

const fmtDateLong = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00:00");
  if (isNaN(d)) return dateStr;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const fmtTimeFlight = (t) => (t ? t.slice(0, 5) : "");

const calcDayDiff = (depDate, arrDate) => {
  if (!depDate || !arrDate) return 0;
  return Math.round((new Date(arrDate + "T00:00:00") - new Date(depDate + "T00:00:00")) / 86400000);
};

const FLIGHT_STATUS_COLORS = {
  Pending:   { color: "#d97706", bg: "#fef3c7" },
  Option:    { color: "#6366f1", bg: "#eef2ff" },
  Confirmed: { color: "#059669", bg: "#ecfdf5" },
  Cancelled: { color: "#dc2626", bg: "#fef2f2" },
  Ticketed:  { color: "#0891b2", bg: "#ecfeff" },
};

const FlightDetail = ({ product }) => {
  const legs = product.legs || [];
  if (legs.length === 0) return <div className={s.detailEmpty}>No flight legs added</div>;

  const isSplit = product.flightPricingMode === "SPLIT";
  const pricingLabel = isSplit ? "Split Flight" : "Full Flight";
  const typeLabel = product.flightType || "—";
  const vatRole = product.supplierCommercialRole === "AGENT" ? "Agent"
    : product.supplierCommercialRole === "MERCHANT" ? "Merchant"
    : product.supplierCommercialRole || "—";
  const bookingCtx = product.flightBookingMode === "within_travel"
    ? "Flight within Travel Booking" : "Flight Only";
  const masterPnr = !isSplit ? (product.airlinePnr || "") : "";

  const outboundLegs = legs.filter(l => l.direction === "Outbound");
  const inboundLegs  = legs.filter(l => l.direction === "Inbound");
  const allLegsOrdered = [...outboundLegs, ...inboundLegs];

  const metaItem = (label, value) => (
    <span style={{ fontSize: 12, color: "var(--text-tertiary)" }}>
      {label}: <strong style={{ color: "var(--text-primary)", fontWeight: 600 }}>{value}</strong>
    </span>
  );

  const cell = { fontSize: 12.5, color: "var(--text-secondary)", whiteSpace: "nowrap", padding: "0 6px" };
  const sep  = { color: "var(--border)", padding: "0 2px", fontSize: 11, userSelect: "none" };

  return (
    <div className={s.productCardBody}>
      {/* Config line */}
      <div style={{
        display: "flex", alignItems: "center", gap: 24, flexWrap: "wrap",
        padding: "10px 18px",
        borderBottom: "1px solid var(--border-light)",
        background: "var(--surface-raised, var(--surface))",
      }}>
        {metaItem("Pricing Mode", pricingLabel)}
        {metaItem("Flight Type", typeLabel)}
        {metaItem("VAT Rol", vatRole)}
        {metaItem("Booking Context", bookingCtx)}
      </div>

      {/* Compact leg rows */}
      {allLegsOrdered.map((leg, idx) => {
        const depCity = leg.departureName || flightCityName(leg.departure);
        const arrCity = leg.arrivalName || flightCityName(leg.arrival);
        const flightCode = [leg.airlineCode, leg.flightNumber].filter(Boolean).join("");
        const dayDiff = calcDayDiff(leg.date, leg.arrivalDate);
        const dayTag = dayDiff > 0 ? ` (+${dayDiff})` : dayDiff < 0 ? ` (${dayDiff})` : "";
        const legPnr = (isSplit
          ? (leg.direction === "Outbound" ? product.outboundPnr : product.inboundPnr)
          : masterPnr) || leg.pnr || "";
        const sc = FLIGHT_STATUS_COLORS[leg.flightStatus] || FLIGHT_STATUS_COLORS.Pending;

        return (
          <div key={leg.id || idx} style={{
            display: "flex", alignItems: "center", gap: 0,
            padding: "8px 18px",
            borderBottom: "1px solid var(--border-light)",
            fontSize: 12.5, color: "var(--text-secondary)",
          }}>
            <span style={{ width: 24, flexShrink: 0, fontWeight: 700, fontSize: 12, color: "var(--text-tertiary)" }}>
              {idx + 1}.
            </span>
            <span style={{ ...cell, fontWeight: 600, color: "var(--text-primary)", minWidth: 120, flexShrink: 0 }}>
              {fmtDateLong(leg.date)}
            </span>
            <span style={sep}>|</span>
            <span style={{ ...cell, display: "flex", alignItems: "center", gap: 4, minWidth: 0, flex: 1 }}>
              <span style={{ fontWeight: 600, color: "var(--text-primary)" }}>
                {depCity} <span style={{ fontWeight: 400, color: "var(--text-tertiary)" }}>({leg.departure})</span>
              </span>
              <span style={{ color: "var(--text-quaternary)", margin: "0 2px" }}>&ndash;</span>
              <span style={{ fontWeight: 600, color: "var(--text-primary)" }}>
                {arrCity} <span style={{ fontWeight: 400, color: "var(--text-tertiary)" }}>({leg.arrival})</span>
              </span>
            </span>
            <span style={sep}>|</span>
            <span style={{ ...cell, fontFamily: "'JetBrains Mono', monospace", fontSize: 11.5, minWidth: 100, flexShrink: 0 }}>
              {fmtTimeFlight(leg.time)} - {fmtTimeFlight(leg.arrivalTime)}
              {dayTag && <span style={{ color: "#d97706", fontWeight: 700, fontSize: 10.5 }}>{dayTag}</span>}
            </span>
            <span style={sep}>|</span>
            <span style={{ ...cell, fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "#3b5bdb", minWidth: 60, flexShrink: 0 }}>
              {flightCode || "—"}
            </span>
            <span style={sep}>|</span>
            <span style={{ ...cell, fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, minWidth: 60, flexShrink: 0 }}>
              {legPnr || "—"}
            </span>
            <span style={sep}>|</span>
            <span style={{ ...cell, minWidth: 70, flexShrink: 0 }}>
              {leg.cabinClass || "—"}
            </span>
            <span style={sep}>|</span>
            <span style={{ ...cell, minWidth: 60, flexShrink: 0 }}>
              {leg.fareClassName || leg.fareClass || "—"}
            </span>
            <span style={sep}>|</span>
            <span style={{
              fontSize: 10.5, fontWeight: 700, padding: "2px 8px", borderRadius: 999,
              background: sc.bg, color: sc.color,
              flexShrink: 0, whiteSpace: "nowrap",
            }}>
              {leg.flightStatus || "Pending"}
            </span>
          </div>
        );
      })}
    </div>
  );
};

/* ─── Hotel Detail ────────────────────────────────────────────────────── */
const HotelDetail = ({ product }) => {
  const hotel  = product.hotel || product.hotelObj;
  const rooms  = (Array.isArray(product.rooms) ? product.rooms : []);
  const nights = calcNights(product.checkIn, product.checkOut);

  return (
    <div className={s.productCardBody}>
      {/* Service dates strip */}
      {product.checkIn && (
        <div className={s.serviceDateStrip}>
          <span className={s.serviceDateStripIcon}>{Icons.calendar}</span>
          <span className={s.serviceDateStripLabel}>Stay</span>
          <span className={s.serviceDateStripVal}>
            {fmtDate(product.checkIn)} → {fmtDate(product.checkOut)}
            {nights > 0 ? ` · ${nights} night${nights !== 1 ? "s" : ""}` : ""}
          </span>
        </div>
      )}
      <div className={s.hotelDetail}>
        {/* Hotel name & stars */}
        {hotel && (
          <div className={s.hotelHeader}>
            <div className={s.hotelHeroIcon}>{Icons.hotel}</div>
            <div>
              <div className={s.hotelName}>{hotel.name}</div>
              <div className={s.hotelMeta}>
                {hotel.stars > 0 && (
                  <span className={s.hotelStars}>
                    {Array.from({ length: hotel.stars }, (_, i) => (
                      <span key={i}>{Icons.starFill}</span>
                    ))}
                  </span>
                )}
                {hotel.city && (
                  <span className={s.hotelLoc}>
                    {Icons.mapPin} {hotel.city}{hotel.country ? `, ${hotel.country}` : ""}
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Date strip with night count */}
        <div className={s.hotelDateStrip}>
          <div className={s.hotelDateBox}>
            <span className={s.hotelDateLabel}>Check-in</span>
            <span className={s.hotelDateDay}>{getDayNum(product.checkIn)}</span>
            <span className={s.hotelDateFull}>{fmtDateFull(product.checkIn)}</span>
          </div>
          <div className={s.hotelNightsDivider}>
            <span className={s.hotelNightsNum}>{nights || "—"}</span>
            <span className={s.hotelNightsLabel}>{nights === 1 ? "Night" : "Nights"}</span>
          </div>
          <div className={`${s.hotelDateBox} ${s.hotelDateBoxRight}`}>
            <span className={s.hotelDateLabel}>Check-out</span>
            <span className={s.hotelDateDay}>{getDayNum(product.checkOut)}</span>
            <span className={s.hotelDateFull}>{fmtDateFull(product.checkOut)}</span>
          </div>
        </div>

        {/* Rooms */}
        {rooms.length > 0 && (
          <div className={s.hotelRoomList}>
            {rooms.map((r, i) => (
              <div key={r.id || i} className={s.hotelRoom}>
                <span className={s.hotelRoomIdx}>R{i + 1}</span>
                <span className={s.hotelRoomName}>
                  {r.roomType || r.roomName || "Room"}
                  {r.roomCharacteristic ? ` · ${r.roomCharacteristic}` : ""}
                </span>
                <div className={s.hotelRoomPills}>
                  {r.boardName && (
                    <Pill bg="#fff4e6" color="#c05621">
                      {r.boardCode ? `${r.boardCode} ` : ""}{r.boardName}
                    </Pill>
                  )}
                  {r.numberOfRooms > 1 && <Pill>×{r.numberOfRooms} rooms</Pill>}
                  {r.roomRate && (
                    <Pill
                      bg={r.roomRate === "Refundable" ? "#ecfdf5" : "#fef2f2"}
                      color={r.roomRate === "Refundable" ? "#065f46" : "#991b1b"}
                    >
                      {r.roomRate}
                    </Pill>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/* ─── Transfer Detail ─────────────────────────────────────────────────── */

const TransferLegRoute = ({ leg, label, labelColor, labelBg }) => {
  if (!leg) return null;
  const pickupTimeDisplay = leg.pickupTimeType === "tba" ? "To Be Advised"
    : leg.pickupTimeType === "voucher" ? "Follow Voucher"
    : leg.pickupTime || null;
  const flightCode = [leg.flightAirlineCode, leg.flightFlightNumber].filter(Boolean).join("");
  return (
    <div className={s.transferLegCard}>
      {label && (
        <span className={s.transferLegLabel} style={{ background: labelBg, color: labelColor }}>
          {label}
        </span>
      )}
      <div className={s.transferRoute}>
        <div className={s.transferLoc}>
          <span className={s.transferLocLabel}>Pickup</span>
          <span className={s.transferLocName}>{leg.pickupLocation || "—"}</span>
          <span className={s.transferLocTime}>
            {fmtDate(leg.pickupDate)}
            {pickupTimeDisplay ? ` · ${pickupTimeDisplay}` : ""}
          </span>
        </div>
        <div className={s.transferMid}>
          <div className={s.transferMidLine} />
          <span className={s.transferMidIcon}>{Icons.bus}</span>
          <div className={s.transferMidLine} />
        </div>
        <div className={`${s.transferLoc} ${s.transferLocRight}`}>
          <span className={s.transferLocLabel}>Drop-off</span>
          <span className={s.transferLocName}>{leg.dropoffLocation || "—"}</span>
        </div>
      </div>
      {flightCode && (
        <div className={s.flightPills}>
          <Pill bg="#eef3ff" color="#3b5bdb">Flight: {flightCode}</Pill>
          {leg.flightDate && <Pill>{fmtDate(leg.flightDate)}</Pill>}
          {leg.flightTime && <Pill>{leg.flightTime}</Pill>}
        </div>
      )}
    </div>
  );
};

const TransferDetail = ({ product }) => {
  const isRoundTrip = product.transferMoment === "RoundTrip";
  const momentLabel = product.transferMoment === "RoundTrip" ? "Round Trip"
    : product.transferMoment === "Arrival" ? "Arrival Transfer"
    : product.transferMoment === "Departure" ? "Departure Transfer"
    : product.transferMoment === "Custom" ? "Custom Transfer"
    : product.transferMoment || "";

  const pickupTimeDisplay = product.pickupTimeType === "tba" ? "To Be Advised"
    : product.pickupTimeType === "voucher" ? "Follow Voucher"
    : product.pickupTime || null;

  const paxParts = [
    product.numberOfAdults ? `${product.numberOfAdults} Adult${product.numberOfAdults > 1 ? "s" : ""}` : null,
    product.numberOfChildren ? `${product.numberOfChildren} Child${product.numberOfChildren > 1 ? "ren" : ""}` : null,
  ].filter(Boolean).join(", ");

  return (
    <div className={s.productCardBody}>
      {/* Config line */}
      <div style={{
        display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
        padding: "10px 18px",
        borderBottom: "1px solid var(--border-light)",
        background: "var(--surface-raised, var(--surface))",
      }}>
        {momentLabel && <Pill bg="#eef3ff" color="#3b5bdb">{momentLabel}</Pill>}
        {product.transferType && <Pill>{product.transferType}</Pill>}
        {product.vehicleType && <Pill>Private {product.vehicleType}</Pill>}
        {paxParts && <Pill bg="#fef3c7" color="#92400e">{paxParts}</Pill>}
        {product.leadTravelerName && <Pill bg="#ecfdf5" color="#065f46">Lead: {product.leadTravelerName}</Pill>}
      </div>

      {/* Service dates strip — single-leg only */}
      {!isRoundTrip && product.pickupDate && (
        <div className={s.serviceDateStrip}>
          <span className={s.serviceDateStripIcon}>{Icons.calendar}</span>
          <span className={s.serviceDateStripLabel}>Transfer</span>
          <span className={s.serviceDateStripVal}>
            {fmtDate(product.pickupDate)}{pickupTimeDisplay ? ` · ${pickupTimeDisplay}` : ""}
          </span>
        </div>
      )}

      <div className={s.transferDetail}>
        {isRoundTrip ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <TransferLegRoute
              leg={product.arrivalLeg}
              label="ARRIVAL"
              labelBg="#dcfce7"
              labelColor="#166534"
            />
            <TransferLegRoute
              leg={product.departureLeg}
              label="DEPARTURE"
              labelBg="#fef3c7"
              labelColor="#92400e"
            />
          </div>
        ) : (
          <>
            <div className={s.transferRoute}>
              <div className={s.transferLoc}>
                <span className={s.transferLocLabel}>Pickup</span>
                <span className={s.transferLocName}>{product.pickupLocation || "—"}</span>
                <span className={s.transferLocTime}>
                  {fmtDate(product.pickupDate)}{pickupTimeDisplay ? ` · ${pickupTimeDisplay}` : ""}
                </span>
              </div>
              <div className={s.transferMid}>
                <div className={s.transferMidLine} />
                <span className={s.transferMidIcon}>{Icons.bus}</span>
                <div className={s.transferMidLine} />
              </div>
              <div className={`${s.transferLoc} ${s.transferLocRight}`}>
                <span className={s.transferLocLabel}>Drop-off</span>
                <span className={s.transferLocName}>{product.dropoffLocation || "—"}</span>
              </div>
            </div>
            {/* Flight link */}
            {(product.flightAirlineCode || product.flightFlightNumber) && (
              <div className={s.flightPills}>
                <Pill bg="#eef3ff" color="#3b5bdb">
                  Flight: {[product.flightAirlineCode, product.flightFlightNumber].filter(Boolean).join("")}
                </Pill>
                {product.flightDate && <Pill>{fmtDate(product.flightDate)}</Pill>}
                {product.flightTime && <Pill>{product.flightTime}</Pill>}
              </div>
            )}
          </>
        )}
      </div>

      {/* Instructions & contact info */}
      {(product.pickupInstructions || product.cantFindInstructions || product.travelDisruptionInfo || product.contactInfoSupplier || product.emergencyAssistance) && (
        <div style={{ padding: "10px 18px", borderTop: "1px solid var(--border-light)", display: "flex", flexWrap: "wrap", gap: "8px 24px" }}>
          {product.pickupInstructions && (
            <div style={{ minWidth: 180, flex: 1 }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: "#9ca3af", textTransform: "uppercase", marginBottom: 2 }}>Pickup Instructions</div>
              <div style={{ fontSize: 12.5, color: "var(--text-primary)" }}>{product.pickupInstructions}</div>
            </div>
          )}
          {product.cantFindInstructions && (
            <div style={{ minWidth: 180, flex: 1 }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: "#9ca3af", textTransform: "uppercase", marginBottom: 2 }}>Can't Find Transfer</div>
              <div style={{ fontSize: 12.5, color: "var(--text-primary)" }}>{product.cantFindInstructions}</div>
            </div>
          )}
          {product.travelDisruptionInfo && (
            <div style={{ minWidth: 180, flex: 1 }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: "#9ca3af", textTransform: "uppercase", marginBottom: 2 }}>Travel Disruption</div>
              <div style={{ fontSize: 12.5, color: "var(--text-primary)" }}>{product.travelDisruptionInfo}</div>
            </div>
          )}
          {product.contactInfoSupplier && (
            <div style={{ minWidth: 180, flex: 1 }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: "#9ca3af", textTransform: "uppercase", marginBottom: 2 }}>Supplier Contact</div>
              <div style={{ fontSize: 12.5, color: "var(--text-primary)" }}>{product.contactInfoSupplier}</div>
            </div>
          )}
          {product.emergencyAssistance && (
            <div style={{ minWidth: 180, flex: 1 }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: "#9ca3af", textTransform: "uppercase", marginBottom: 2 }}>Emergency Assistance</div>
              <div style={{ fontSize: 12.5, color: "var(--text-primary)" }}>{product.emergencyAssistance}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ─── Generic Detail (Insurance, Package, CarRental, Visa, Cruise) ──── */
const GenericDetail = ({ product }) => {
  const rows = [
    ["Coverage",    product.coverageType || product.insuranceType],
    ["From",        product.startDate    || product.coverageStartDate],
    ["To",          product.endDate      || product.coverageEndDate],
    ["Package",     product.packageType],
    ["Car Category",product.carCategory],
    ["Pickup",      product.pickupLocation || product.pickupCity],
    ["Drop-off",    product.dropoffLocation || product.dropoffCity],
    ["Destination", product.destination],
    ["Visa Type",   product.visaType],
    ["Entry",       product.entryType],
    ["Ship",        product.shipName],
    ["Cabin",       product.cabinType],
    ["Embarkation", product.embarkationPort],
    ["Disembark",   product.disembarkationPort],
    ["Ports",       Array.isArray(product.ports) ? product.ports.map(p => p.portName).filter(Boolean).join(" → ") : null],
    ["Duration",    product.duration],
    ["Provider",    product.provider || product.insuranceProvider],
    ["Policy #",    product.policyNumber],
  ].filter(([, v]) => v);

  if (!rows.length) return null;

  return (
    <div className={s.productCardBody}>
      {(product.startDate || product.coverageStartDate) && (
        <div className={s.serviceDateStrip}>
          <span className={s.serviceDateStripIcon}>{Icons.calendar}</span>
          <span className={s.serviceDateStripLabel}>Period</span>
          <span className={s.serviceDateStripVal}>
            {fmtDate(product.startDate || product.coverageStartDate)}
            {(product.endDate || product.coverageEndDate)
              ? ` → ${fmtDate(product.endDate || product.coverageEndDate)}`
              : ""}
          </span>
        </div>
      )}
      <div className={s.genericGrid}>
        {rows.map(([label, val]) => (
          <div key={label} className={s.genericField}>
            <span className={s.genericLabel}>{label}</span>
            <span className={s.genericVal}>
              {String(val).includes("-") && !["Visa Type","Ports","Policy #"].includes(label)
                ? fmtDate(val) : val}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   PRODUCT ROW FORMATTERS (Booking Overview list)
   ═══════════════════════════════════════════════════════════════════════════ */

/* Headline shown as the product row title */
const productHeadline = (p) => {
  if (p.type === "Hotel") return p.hotel?.name || p.hotelObj?.name || p.hotelName || "Hotel";
  return PRODUCT_SHORT_LABELS[p.type] || p.type;
};

/* Secondary route / description line */
const productRoute = (p) => {
  if (p.type === "Flight") {
    const legs = p.legs || [];
    if (!legs.length) return "";
    const codes = [];
    legs.forEach((l) => {
      if (l.departure && codes[codes.length - 1] !== l.departure) codes.push(l.departure);
      if (l.arrival) codes.push(l.arrival);
    });
    return codes.join(" → ");
  }
  if (p.type === "Hotel") {
    const h = p.hotel || p.hotelObj;
    const room = (Array.isArray(p.rooms) ? p.rooms : [])[0];
    const roomLine = room
      ? `${p.rooms.length} × ${room.roomType || room.roomName || "Room"}${room.boardName ? ` · ${room.boardName}` : ""}`
      : null;
    return [h?.city, roomLine].filter(Boolean).join("  ·  ");
  }
  if (p.type === "Transfer") {
    const pickup = p.pickupLocation || p.arrivalLeg?.pickupLocation;
    const dropoff = p.dropoffLocation || p.arrivalLeg?.dropoffLocation;
    return [pickup, dropoff].filter(Boolean).join(" → ");
  }
  return [p.coverageType, p.insuranceType, p.destination, p.carCategory, p.visaType, p.packageType]
    .filter(Boolean).join("  ·  ");
};

/* Service date line */
const productDateLine = (p) => {
  if (p.type === "Flight") {
    const ds = (p.legs || []).map((l) => l.date).filter(Boolean).sort();
    if (!ds.length) return "";
    return ds.length > 1 ? `${fmtDate(ds[0])} → ${fmtDate(ds[ds.length - 1])}` : fmtDate(ds[0]);
  }
  if (p.type === "Hotel") {
    if (!p.checkIn) return "";
    const n = calcNights(p.checkIn, p.checkOut);
    return `${fmtDate(p.checkIn)} → ${fmtDate(p.checkOut)}${n ? ` (${n} Night${n !== 1 ? "s" : ""})` : ""}`;
  }
  if (p.type === "Transfer") {
    const d = p.pickupDate || p.arrivalLeg?.pickupDate;
    return d ? `${fmtDate(d)}${p.pickupTime ? ` · ${p.pickupTime}` : ""}` : "";
  }
  const start = p.startDate || p.coverageStartDate;
  const end = p.endDate || p.coverageEndDate;
  return [start && fmtDate(start), end && fmtDate(end)].filter(Boolean).join(" → ");
};

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════════════════════ */

const StepSummary = ({ data, bookingRef, onGoToStep, onSubmit, onBack, submitting }) => {
  const { showToast } = useToast();
  const [copied, setCopied] = useState(false);
  const [expandedIds, setExpandedIds] = useState([]);

  /* ── Customer data ── */
  const customer    = data._prefillCustomer || data.customer;
  const newCust     = data.newCustomer;
  const custName    = customer?.name  || [newCust.firstName, (newCust.lastName || "").toUpperCase()].filter(Boolean).join(" ") || "—";
  const custEmail   = customer?.email || newCust.email   || "—";
  const custPhone   = customer?.phone || newCust.phone   || "—";

  /* ── Pricing calcs ── */
  // SSR pricing is stored per-unit; qty is on the ssrItem itself → must multiply
  const ssrKeyMap = {};
  (data.ssrItems || []).forEach((ssr, idx) => {
    ssrKeyMap[`ssr-${ssr.itemId}-${ssr.travelerId ?? idx}`] = ssr.qty || 1;
  });

  let totalSelling = 0, totalCost = 0, totalVAT = 0;
  Object.entries(data.pricing).forEach(([key, p]) => {
    const qty = ssrKeyMap[key] ?? 1;   // 1 for all non-SSR entries
    totalSelling += (p.sellingPrice || 0) * qty;
    totalCost    += (p.costPrice    || 0) * qty;
    totalVAT     += (p.vatTax || 0) * qty;
  });

  const adjustmentsTotal = (data.adjustments || []).reduce((sum, a) => {
    const val = a.mode === "Fixed" ? (a.value || 0) : (totalSelling * (a.value || 0)) / 100;
    return sum + (a.type === "Discount" ? -val : val);
  }, 0);
  const finalSelling = totalSelling + adjustmentsTotal;
  const margin       = totalSelling - totalCost;
  const marginPct    = totalSelling > 0 ? ((margin / totalSelling) * 100).toFixed(1) : "0.0";

  /* ── Status ── */
  const allConfirmed  = data.products.length > 0 && data.products.every(p => p.status === "Confirmed");
  const derivedStatus = allConfirmed ? "Confirmed" : "Pending";

  /* ── Pax counts ── */
  const paxCounts = data.travelers.reduce((acc, t) => {
    const type = classifyTraveler(t.dob);
    acc[type] = (acc[type] || 0) + 1;
    return acc;
  }, {});

  /* ── Assignments by product ── */
  const assignByProduct = {};
  (data.assignments || []).forEach(a => {
    if (!assignByProduct[a.productId]) assignByProduct[a.productId] = [];
    assignByProduct[a.productId].push(a.travelerIndex);
  });

  /* ── Pricing by product ── */
  const pricingByProduct = {};
  Object.entries(data.pricing).forEach(([key, val]) => {
    const firstDash = key.indexOf("-");
    const travIdx   = parseInt(key.slice(0, firstDash), 10);
    const remainder = key.slice(firstDash + 1);
    const prdMatch  = remainder.match(/^(PRD-\d+)/);
    const productId = prdMatch ? prdMatch[1] : remainder;
    if (!pricingByProduct[productId]) pricingByProduct[productId] = [];
    pricingByProduct[productId].push({ travelerIndex: travIdx, key, ...val });
  });

  /* ── Copy ref handler ── */
  const handleCopy = () => {
    navigator.clipboard?.writeText(bookingRef);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  /* ══════════════════ BOOKING OVERVIEW derived values ══════════════════ */

  /* Travel dates — earliest / latest service date across all products */
  const serviceDates = [];
  const pushDate = (d) => { if (d && /^\d{4}-\d{2}-\d{2}/.test(d)) serviceDates.push(d.slice(0, 10)); };
  data.products.forEach((p) => {
    (p.legs || []).forEach((l) => { pushDate(l.date); pushDate(l.arrivalDate); });
    pushDate(p.checkIn); pushDate(p.checkOut);
    pushDate(p.pickupDate); pushDate(p.arrivalDate);
    pushDate(p.arrivalLeg?.pickupDate); pushDate(p.departureLeg?.pickupDate);
    pushDate(p.startDate); pushDate(p.coverageStartDate);
    pushDate(p.endDate); pushDate(p.coverageEndDate);
  });
  serviceDates.sort();
  const tripStart = serviceDates[0] || null;
  const tripEnd = serviceDates[serviceDates.length - 1] || null;
  const tripNights = tripStart && tripEnd ? calcNights(tripStart, tripEnd) : 0;
  const tripDays = tripNights > 0 ? tripNights + 1 : 0;

  /* Pax summary string e.g. "2 Adults, 1 Child, 1 Infant" */
  const paxSummary = PAX_ORDER.filter((t) => paxCounts[t]).map((t) => {
    const n = paxCounts[t];
    return `${n} ${PAX_NOUN[t][n === 1 ? 0 : 1]}`;
  }).join(", ") || `${data.travelers.length} Traveler${data.travelers.length !== 1 ? "s" : ""}`;

  /* Financials — no payments are registered during creation, so paid = 0 */
  const paidAmount = (data.payments || []).reduce((sum, p) => sum + (p.amount || 0), 0);
  const outstanding = Math.max(0, finalSelling - paidAmount);
  const paidPct = finalSelling > 0 ? (paidAmount / finalSelling) * 100 : 0;
  const outstandingPct = finalSelling > 0 ? (outstanding / finalSelling) * 100 : 0;
  const markupCommission = margin - totalVAT;
  const pct1 = (n) => `${n.toFixed(1).replace(".", ",")}`;

  /* Booking type & VAT mode labels (domain-derived) */
  const isPackage = data.saleType === "PACKAGE_TRAVEL";
  const bookingTypeLabel = isPackage ? "Package Booking" : "Separate Services";
  const vatModeLabel = isPackage ? "Margin (TOMS)" : "Standard";

  /* Per-product selling total (sums all priced entries for the product) */
  const productSellingTotal = (p) =>
    (pricingByProduct[p.id] || []).reduce((sum, e) => sum + (e.sellingPrice || 0), 0);

  /* Warnings — any product not yet Confirmed */
  const warnings = data.products
    .filter((p) => p.status && p.status !== "Confirmed")
    .map((p) => `${productHeadline(p)} is currently ${(p.status || "pending").toLowerCase()}`);

  /* Validation & checks */
  const travelersComplete = data.travelers.length > 0 && data.travelers.every((t) => t.firstName && t.lastName && t.dob);
  const allAssigned = data.products.length > 0 && (data.assignments || []).length > 0;
  const allPriced = data.products.length > 0 && Object.keys(data.pricing).length > 0;
  const allSupplierRefs = data.products.length > 0 && data.products.every((p) => p.supplier || p.reference || p.supplierRef);
  const overviewChecks = [
    { label: "Travelers", desc: travelersComplete ? "All travelers completed" : "Some travelers incomplete", done: travelersComplete },
    { label: "Products & Assignments", desc: allAssigned ? "All products assigned" : "Assignments missing", done: allAssigned },
    { label: "Pricing", desc: allPriced ? "All products priced" : "Pricing missing", done: allPriced },
    { label: "Supplier References", desc: allSupplierRefs ? "All references entered" : "References missing", done: allSupplierRefs },
    { label: "VAT & Taxes", desc: allPriced ? "Calculated" : "Pending pricing", done: allPriced },
    { label: "Documents", desc: "Ready to generate", done: true },
  ];
  const readyToFinalize = overviewChecks.every((c) => c.done) && warnings.length === 0;

  /* Last updated stamp */
  const lastUpdated = new Date().toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false });

  /* Expand / collapse product rows */
  const toggleExpand = (id) =>
    setExpandedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const allExpanded = data.products.length > 0 && expandedIds.length === data.products.length;
  const toggleExpandAll = () => setExpandedIds(allExpanded ? [] : data.products.map((p) => p.id));

  /* Footer button handlers */
  const handleSaveDraft = () => showToast("Draft saved", "success");
  const handleNotReady = (label) =>
    showToast(`${label} will be available after the booking is finalized`, "error");

  return (
    <div className={s.bo}>

      {/* ═══════════════════════════════════════════════ OVERVIEW HEADER */}
      <div className={s.boHead}>
        <div>
          <h2 className={s.boHeadTitle}>Booking Overview</h2>
          <p className={s.boHeadSub}>Review all booking details before finalizing</p>
        </div>
        <div className={s.boHeadRight}>
          <div className={s.boHeadStatusWrap}>
            <span className={s.boHeadStatusLabel}>Booking Status</span>
            {readyToFinalize ? (
              <span className={s.boReadyBadge}>{Icons.check} Ready to Finalize</span>
            ) : (
              <Badge label={derivedStatus} />
            )}
          </div>
          <span className={s.boHeadUpdated}>Last updated: {lastUpdated}</span>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════ TOP SUMMARY CARD */}
      <div className={s.boTopCard}>
        {/* Booking */}
        <div className={s.boTopCell}>
          <div className={s.boTopCellHead}>
            <span className={s.boTopIcon}>{Icons.box}</span>
            <span className={s.boTopLabel}>Booking</span>
          </div>
          <div className={s.boTopRefRow}>
            <span className={s.boTopRef}>{bookingRef || "—"}</span>
            {!copied ? (
              <button className={s.boCopyBtn} onClick={handleCopy} title="Copy reference">{Icons.copy}</button>
            ) : (
              <span className={s.boCopied}>Copied!</span>
            )}
          </div>
        </div>

        {/* Customer */}
        <div className={s.boTopCell}>
          <div className={s.boTopCellHead}><span className={s.boTopLabel}>Customer</span></div>
          <div className={s.boTopCustName}>{Icons.user} {custName}</div>
          {custEmail !== "—" && <div className={s.boTopCustLine}>✉ {custEmail}</div>}
          {custPhone !== "—" && <div className={s.boTopCustLine}>📞 {custPhone}</div>}
        </div>

        {/* Travel dates */}
        <div className={s.boTopCell}>
          <div className={s.boTopCellHead}><span className={s.boTopLabel}>Travel Dates</span></div>
          <div className={s.boTopDates}>
            {Icons.calendar} {tripStart ? `${fmtDate(tripStart)} — ${fmtDate(tripEnd)}` : "—"}
          </div>
          {tripNights > 0 && (
            <div className={s.boTopDatesSub}>
              {tripNights} Night{tripNights !== 1 ? "s" : ""} / {tripDays} Day{tripDays !== 1 ? "s" : ""}
            </div>
          )}
        </div>

        {/* Travelers */}
        <div className={s.boTopCell}>
          <div className={s.boTopCellHead}><span className={s.boTopLabel}>Travelers</span></div>
          <div className={s.boTopTravelers}>{Icons.users} {paxSummary}</div>
          {onGoToStep && (
            <button className={s.boOutlineBtn} onClick={() => onGoToStep(3)}>View Travelers</button>
          )}
        </div>

        {/* Booking type */}
        <div className={`${s.boTopCell} ${s.boTopCellLast}`}>
          <div className={s.boTopCellHead}><span className={s.boTopLabel}>Booking Type</span></div>
          <div className={s.boTopType}>{Icons.box} {bookingTypeLabel}</div>
          <div className={s.boTopVat}>VAT MODE: <span className={s.boVatBadge}>{vatModeLabel}</span></div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════ KPI CARDS */}
      <div className={s.boKpis}>
        <div className={`${s.boKpi} ${s.boKpiBlue}`}>
          <div className={s.boKpiHead}>
            <span className={s.boKpiLabel}>Total Selling Price</span>
            <span className={s.boKpiIcon}>{Icons.fileText}</span>
          </div>
          <div className={s.boKpiVal}>{formatCurrency(finalSelling)}</div>
          <div className={s.boKpiSub}>Incl. VAT</div>
        </div>
        <div className={`${s.boKpi} ${s.boKpiNeutral}`}>
          <div className={s.boKpiHead}>
            <span className={s.boKpiLabel}>Total Purchase Price</span>
            <span className={s.boKpiIcon}>{Icons.wallet}</span>
          </div>
          <div className={s.boKpiVal}>{formatCurrency(totalCost)}</div>
          <div className={s.boKpiSub}>Incl. VAT</div>
        </div>
        <div className={`${s.boKpi} ${s.boKpiGreen}`}>
          <div className={s.boKpiHead}>
            <span className={s.boKpiLabel}>Gross Margin</span>
            <span className={s.boKpiIcon}>{Icons.dollarSign}</span>
          </div>
          <div className={s.boKpiVal}>{formatCurrency(margin)}</div>
          <div className={s.boKpiSub}>{pct1(parseFloat(marginPct))}%</div>
        </div>
        <div className={`${s.boKpi} ${s.boKpiAmber}`}>
          <div className={s.boKpiHead}>
            <span className={s.boKpiLabel}>Outstanding Balance</span>
            <span className={s.boKpiIcon}>{Icons.wallet}</span>
          </div>
          <div className={s.boKpiVal}>{formatCurrency(outstanding)}</div>
          <div className={s.boKpiSub}>{pct1(outstandingPct)}% of total</div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════ TRAVELERS SUMMARY */}
      <div className={s.boCard}>
        <div className={s.boCardHead}>
          <div className={s.boCardHeadLeft}>
            <span className={s.boCardIcon}>{Icons.users}</span>
            <span className={s.boCardTitle}>Travelers Summary</span>
          </div>
          <div className={s.boCardHeadRight}>
            <span className={s.boCardHeadMeta}>Total: {data.travelers.length} Travelers</span>
            {onGoToStep && (
              <button className={s.boOutlineBtn} onClick={() => onGoToStep(3)}>View &amp; Manage Travelers</button>
            )}
          </div>
        </div>
        <div className={s.boTravSummary}>
          <div className={s.boPaxRow}>
            {PAX_ORDER.filter((t) => paxCounts[t]).map((t) => {
              const pc = PAX_PALETTE[t];
              return (
                <div key={t} className={s.boPaxItem}>
                  <span className={s.boPaxIcon} style={{ background: pc.bg, color: pc.color }}>{Icons.user}</span>
                  <div>
                    <div className={s.boPaxCount}>{paxCounts[t]} {PAX_NOUN[t][paxCounts[t] === 1 ? 0 : 1]}</div>
                    <div className={s.boPaxDesc}>{PAX_OV_DESC[t]}</div>
                  </div>
                </div>
              );
            })}
            {Object.keys(paxCounts).length === 0 && (
              <div className={s.boPaxDesc}>No travelers added yet</div>
            )}
          </div>
          <div className={`${s.boCompleteBox} ${travelersComplete ? s.boCompleteOk : s.boCompleteWarn}`}>
            <span className={s.boCompleteIcon}>{travelersComplete ? Icons.check : "!"}</span>
            <div>
              <div className={s.boCompleteTitle}>
                {travelersComplete ? "All travelers are complete" : "Some travelers are incomplete"}
              </div>
              <div className={s.boCompleteSub}>
                {travelersComplete
                  ? "Passports, personal details and contacts are completed."
                  : "Complete passport and personal details for all travelers."}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════ PRODUCTS & SERVICES */}
      <div className={s.boCard}>
        <div className={s.boCardHead}>
          <div className={s.boCardHeadLeft}>
            <span className={s.boCardIcon}>{Icons.box}</span>
            <span className={s.boCardTitle}>Products &amp; Services</span>
          </div>
          <div className={s.boCardHeadRight}>
            <span className={s.boCardHeadMeta}>{data.products.length} Products</span>
            {data.products.length > 0 && (
              <button className={s.boOutlineBtn} onClick={toggleExpandAll}>
                {allExpanded ? "Collapse All" : "Expand All"}
              </button>
            )}
          </div>
        </div>
        <div className={s.boProductList}>
          {data.products.length === 0 ? (
            <div className={s.boEmpty}>No products added yet.</div>
          ) : (
            data.products.map((product) => {
              const colors = PRODUCT_COLOR_MAP[product.type] || { bg: "#f0f1f4", color: "#525866", border: "#e2e4e9" };
              const icon = PRODUCT_ICON_MAP[product.type] || Icons.box;
              const paxNums = (assignByProduct[product.id] || []).length;
              const expanded = expandedIds.includes(product.id);
              const sell = productSellingTotal(product);
              return (
                <div key={product.id} className={s.boProduct}>
                  <div className={s.boProductRow} onClick={() => toggleExpand(product.id)}>
                    <span className={s.boProductIcon} style={{ background: colors.bg, color: colors.color }}>{icon}</span>
                    <div className={s.boProductMain}>
                      <div className={s.boProductName}>{productHeadline(product)}</div>
                      {productRoute(product) && <div className={s.boProductSub}>{productRoute(product)}</div>}
                      {productDateLine(product) && <div className={s.boProductDate}>{productDateLine(product)}</div>}
                    </div>
                    <div className={s.boProductSupplier}>
                      <div className={s.boProductSupName}>{product.supplier || "—"}</div>
                      {product.reference && <div className={s.boProductSupRef}>Ref: {product.reference}</div>}
                      {product.supplierRef && <div className={s.boProductSupRef}>Sup Ref: {product.supplierRef}</div>}
                    </div>
                    <div className={s.boProductStatus}><Badge label={product.status || "Pending"} /></div>
                    <div className={s.boProductPax}>
                      <div className={s.boProductPaxLabel}>Travelers</div>
                      <div className={s.boProductPaxVal}>{paxNums} Traveler{paxNums !== 1 ? "s" : ""}</div>
                    </div>
                    <div className={s.boProductPrice}>
                      <div className={s.boProductPriceVal}>{formatCurrency(sell)}</div>
                      <div className={s.boProductPriceLabel}>Selling Price</div>
                    </div>
                    <span className={`${s.boChevron} ${expanded ? s.boChevronOpen : ""}`}>{Icons.chevronDown}</span>
                  </div>
                  {expanded && (
                    <div className={s.boProductExpand}>
                      {product.type === "Flight" && <FlightDetail product={product} />}
                      {product.type === "Hotel" && <HotelDetail product={product} />}
                      {product.type === "Transfer" && <TransferDetail product={product} />}
                      {!["Flight", "Hotel", "Transfer"].includes(product.type) && <GenericDetail product={product} />}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════ PRICING SUMMARY */}
      <div className={s.boCard}>
        <div className={s.boCardHead}>
          <div className={s.boCardHeadLeft}>
            <span className={s.boCardIcon}>{Icons.wallet}</span>
            <span className={s.boCardTitle}>Pricing Summary</span>
          </div>
          <div className={s.boCardHeadRight}>
            {onGoToStep && (
              <button className={s.boOutlineBtn} onClick={() => onGoToStep(6)}>View Price Breakdown</button>
            )}
          </div>
        </div>
        <div className={s.boPricing}>
          <div className={s.boPricingRows}>
            <div className={s.boPricingRow}><span>Total Selling Price (Incl. VAT)</span><strong>{formatCurrency(finalSelling)}</strong></div>
            <div className={s.boPricingRow}><span>Total Purchase Price (Incl. VAT)</span><strong>{formatCurrency(totalCost)}</strong></div>
            <div className={s.boPricingRow}><span>Total Markup / Commission</span><strong>{formatCurrency(markupCommission)}</strong></div>
            <div className={s.boPricingRow}><span>Total VAT</span><strong>{formatCurrency(totalVAT)}</strong></div>
          </div>
          <div className={s.boMarginBox}>
            <div className={s.boMarginLabel}>Gross Margin</div>
            <div className={s.boMarginVal}>{formatCurrency(margin)}</div>
            <div className={s.boMarginPct}>{pct1(parseFloat(marginPct))}% of selling price</div>
            <svg className={s.boSpark} viewBox="0 0 120 40" preserveAspectRatio="none">
              <polyline points="0,32 20,28 40,30 60,18 80,22 100,10 120,6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════ VALIDATION & CHECKS */}
      <div className={s.boCard}>
        <div className={s.boCardHead}>
          <div className={s.boCardHeadLeft}>
            <span className={s.boCardIcon}>{Icons.shield}</span>
            <span className={s.boCardTitle}>Validation &amp; Checks</span>
          </div>
        </div>
        <div className={s.boChecks}>
          {overviewChecks.map((c) => (
            <div key={c.label} className={s.boCheck}>
              <span className={`${s.boCheckIcon} ${c.done ? s.boCheckIconOk : s.boCheckIconWarn}`}>
                {c.done ? Icons.check : "!"}
              </span>
              <div>
                <div className={s.boCheckTitle}>{c.label}</div>
                <div className={s.boCheckDesc}>{c.desc}</div>
              </div>
            </div>
          ))}
        </div>
        {warnings.length > 0 && (
          <div className={s.boWarning}>
            <span className={s.boWarningIcon}>!</span>
            <div className={s.boWarningBody}>
              <div className={s.boWarningTitle}>{warnings.length} Warning{warnings.length !== 1 ? "s" : ""}</div>
              <ul className={s.boWarningList}>
                {warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
            {onGoToStep && (
              <button className={s.boOutlineBtn} onClick={() => onGoToStep(2)}>View All Warnings</button>
            )}
          </div>
        )}
      </div>

      {/* ═══════════════════════════════════════════════════ FINANCIAL STATUS */}
      <div className={s.boCard}>
        <div className={s.boCardHead}>
          <div className={s.boCardHeadLeft}>
            <span className={s.boCardIcon}>{Icons.dollarSign}</span>
            <span className={s.boCardTitle}>Financial Status</span>
          </div>
        </div>
        <div className={s.boFinance}>
          <div className={s.boFinItem}>
            <div className={s.boFinLabel}>Booking Total</div>
            <div className={s.boFinVal}>{formatCurrency(finalSelling)}</div>
          </div>
          <div className={s.boFinItem}>
            <div className={s.boFinLabel}>Paid Amount</div>
            <div className={s.boFinVal}>{formatCurrency(paidAmount)}</div>
            <div className={s.boFinSub}>{pct1(paidPct)}%</div>
          </div>
          <div className={s.boFinItem}>
            <div className={s.boFinLabel}>Outstanding Balance</div>
            <div className={`${s.boFinVal} ${s.boFinValWarn}`}>{formatCurrency(outstanding)}</div>
            <div className={s.boFinSub}>{pct1(outstandingPct)}%</div>
          </div>
          <div className={s.boFinItem}>
            <div className={s.boFinLabel}>Currency</div>
            <div className={s.boFinVal}>EUR</div>
          </div>
          <div className={s.boFinAction}>
            {onGoToStep && (
              <button className={s.boOutlineBtn} onClick={() => onGoToStep(6)}>View Payment Details</button>
            )}
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════ FOOTER ACTIONS */}
      <div className={s.boFooter}>
        <button className={s.boGhostBtn} onClick={handleSaveDraft}>{Icons.save} Save Draft</button>
        <button className={s.boGhostBtn} onClick={() => onBack && onBack()}>{Icons.edit} Back to Edit</button>
        <div className={s.boFooterSpacer} />
        <button className={s.boBtnPrimary} onClick={() => onSubmit && onSubmit()} disabled={submitting}>
          {Icons.check} {submitting ? "Finalizing..." : "Finalize Booking"}
        </button>
        <button className={s.boBtnOutline} onClick={() => handleNotReady("Document generation")}>
          {Icons.fileText} Generate Documents
        </button>
        <button className={s.boBtnOutline} onClick={() => handleNotReady("Invoice creation")}>
          {Icons.fileText} Create Invoice
        </button>
      </div>
    </div>
  );
};

export default StepSummary;